/* eslint-disable jsx-a11y/anchor-is-valid */
import { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import { useFormik } from 'formik'
import { getSubUsers, getUserByToken, login, setAccess } from '../core/_requests'
import { useNavigate } from "react-router-dom";
import { useAuth } from '../core/Auth'
import { Recaptcha } from '../../../commons/widgets/ReCaptcha'
import { useAppDispatch } from '../../../redux/hook'
import { emptyCart } from '../../../redux/slices/shopping/shoppingSlice'
import { resetArrMQTTResponses } from '../../../redux/slices/device/deviceSlice'
import { resetContentLandscape } from '../../../redux/slices/content/contentSlice'

const loginSchema = Yup.object().shape({
  email: Yup.string()
    //.email('Wrong email format')
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Username is characters'),
  password: Yup.string()
    .min(6, 'Minimum 6 characters')
    .max(50, 'Maximum 50 characters')
    .required('Password is required'),
})

const initialValues = {
  email: '',
  password: '',
}

const API_URL: string | undefined = process.env.REACT_APP_API3_URL
/*
  Formik+YUP+Typescript:
  https://jaredpalmer.com/formik/docs/tutorial#getfieldprops
  https://medium.com/@maurice.de.beijer/yup-validation-and-typescript-and-formik-6c342578a20e
*/

export function Login() {
  const dispatch = useAppDispatch()
  // -----------------------------------------
  
  const [loading, setLoading] = useState(false)
  const { saveMaster, saveAuth, setCurrentUser } = useAuth()
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true)
      try {
        const { data: auth } = await login(values.email, values.password)
        let tmpAuth = auth
        saveAuth(auth)
        if (auth.token_type === "Temporary") {
          navigate('/auth/passcode');
        } else {
          if (auth.token_type === "ready") {
            const { data: auth } = await setAccess(tmpAuth.access_token)
            saveAuth(auth)
            if (auth.account_type === "super" || auth.account_type === "master") {
              const { data: master } = await getSubUsers(auth.access_token)
              if (master.code === 200) {
                saveMaster(master.data)
              }
            }
          }
          const { data: user } = await getUserByToken(auth.access_token)
          setCurrentUser(user)

          dispatch(resetContentLandscape());
          dispatch(emptyCart())
          dispatch(resetArrMQTTResponses())
        }                    
        
      } catch (error) {
        saveAuth(undefined)
        saveMaster(undefined)
        setStatus('The login details are incorrect')
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  // ---------------------------------------
  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_login_signin_form'
    >
      {/* begin::Heading */}
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Sign In</h1>
      </div>
      
      {formik.status ? (
        <div className='mb-lg-15 alert alert-danger'>
          <div className='alert-text font-weight-bold'>{formik.status}</div>
        </div>
      ) : (
        <>
        </>
      )}

      {/* begin::Form group */}
      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
        <input
          placeholder='Email'
          {...formik.getFieldProps('email')}
          className={clsx(
            'form-control bg-transparent',
            { 'is-invalid': formik.touched.email && formik.errors.email },
            {
              'is-valid': formik.touched.email && !formik.errors.email,
            }
          )}
          type='text'
          name='email'
          autoComplete='off'
        />
        {formik.touched.email && formik.errors.email && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.email}</span>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Form group */}
      <div className='fv-row mb-3'>
        <label className='form-label fw-bolder text-dark fs-6 mb-0'>Password</label>
        <input
          placeholder='Password'
          type='password'
          autoComplete='off'
          {...formik.getFieldProps('password')}
          className={clsx(
            'form-control bg-transparent',
            {
              'is-invalid': formik.touched.password && formik.errors.password,
            },
            {
              'is-valid': formik.touched.password && !formik.errors.password,
            }
          )}
        />
        {formik.touched.password && formik.errors.password && (
          <div className='fv-plugins-message-container'>
            <div className='fv-help-block'>
              <span role='alert'>{formik.errors.password}</span>
            </div>
          </div>
        )}
      </div>
      {/* end::Form group */}

      {/* begin::Wrapper */}
      <div className='d-flex flex-stack flex-wrap gap-3 fs-base fw-semibold mb-8'>
        <div />

        {/* begin::Link */}
        <Link to='/auth/forgot-password' className='link-primary'>
          Forgot Password?
        </Link>
        {/* end::Link */}
      </div>
      {/* end::Wrapper */}

      {/* begin::Action */}
      <div className='d-grid mb-10'>
        <button
          type='submit'
          id='kt_sign_in_submit'
          className='btn btn-gradient-primary'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span className='indicator-label'>Log in</span>}
          {loading && (
            <span className='indicator-progress' style={{ display: 'block' }}>
              Please wait...
              <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
            </span>
          )}
        </button>

        <div className='separator border-gray-300 my-10 mx-18'>
          <div className='position-relative start-50 mt-n3 ms-n5 bg-gray-200 w-30px text-center text-gray-400'>Or</div>
        </div>
        <div className='row'>
          <div className='col-12'>
            <a href={`${API_URL}/external/google`} className='btn btn-secondary w-100'>
              <img src='/media/_signin/google-icon.webp' className='w-25px h-auto me-2' />
              <span>Continue with Google</span>
            </a>
          </div>
          {/*
          <div className='col-6'>
            <a href={`${API_URL}/external/facebook`} className='btn btn-secondary '>
              <img src='/media/_signin/facebook-icon.png' className='w-25px h-auto me-2' />
              <span>Continue with Facebook</span>
            </a>
          </div>
          */}
        </div>

      </div>
      {/* end::Action */}

      <div className='text-gray-500 text-center fw-semibold fs-6'>
        Don't have an account?{' '}
        <Link to='/auth/registration' className='link-primary'>
          Sign up for a free trial
        </Link>
      </div>
      <Recaptcha pageName='login' />
    </form>
  )
}
